import React, { useState, useEffect } from 'react'
import { Table, Button, Tag, Modal, Input, Select, Space, Tooltip, Popconfirm, Spin, Card, Row, Col, Statistic, Typography, Divider } from 'antd'
import { SearchOutlined, EyeOutlined, HomeOutlined, MedicineBoxOutlined, ReloadOutlined, FilterOutlined, LogoutOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import http from '../../services/httpServiceHealthCare'
import UserInfo from '../../models/UserInfo'

const { Search } = Input
const { Option } = Select
const { Title, Text } = Typography

const Container = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;
`

const HeaderCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  
  .ant-card-body {
    padding: 24px;
  }
  
  .ant-statistic-title {
    color: rgba(255,255,255,0.8) !important;
  }
  
  .ant-statistic-content {
    color: white !important;
  }
`

const FilterCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  .ant-card-body {
    padding: 20px;
  }
`

const TableCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  
  .ant-card-body {
    padding: 0;
  }
  
  .ant-table {
    border-radius: 12px;
  }
  
  .ant-table-thead > tr > th {
    background: #fafafa;
    border-bottom: 2px solid #e8e8e8;
    font-weight: 600;
    color: #1890ff;
  }
  
  .ant-table-tbody > tr:hover > td {
    background: #f0f8ff !important;
  }
`

const StatusTag = styled(Tag)`
  border-radius: 6px;
  font-weight: 500;
  padding: 4px 12px;
  border: none;
  
  &.sent_to_pharmacy {
    background: #fff7e6;
    color: #fa8c16;
    border: 1px solid #ffd591;
  }
  
  &.medicine_delivered {
    background: #f6ffed;
    color: #52c41a;
    border: 1px solid #b7eb8f;
  }
`

const ActionButton = styled(Button)`
  border-radius: 6px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
`

const LogoutButton = styled(Button)`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  border-radius: 8px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0,0,0,0.2);
  }
`

const PharmacyQueue = (props) => {
    const { history } = props
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [viewModalVisible, setViewModalVisible] = useState(false)
    const [drugModalVisible, setDrugModalVisible] = useState(false)
    const [selectedRecord, setSelectedRecord] = useState(null)
    const [selectedVisitRecord, setSelectedVisitRecord] = useState(null)
    const [patientVisitData, setPatientVisitData] = useState(null)
    const [loadingVisitData, setLoadingVisitData] = useState(false)


    useEffect(() => {
        fetchPharmacyQueue()
    }, [])

    const fetchPharmacyQueue = async () => {
        try {
            setLoading(true)
            const response = await http.get('/PatientRegistrations?filter[where][or][0][status]=sent_to_pharmacy&filter[where][or][1][status]=medicine_delivered')
            setData(response.data || [])
        } catch (error) {
            console.error('Error fetching pharmacy queue:', error)
        } finally {
            setLoading(false)
        }
    }

    const getStatusTag = (status) => {
        switch (status) {
            case 'sent_to_pharmacy':
                return <StatusTag className="sent_to_pharmacy">รอส่งยา</StatusTag>
            case 'medicine_delivered':
                return <StatusTag className="medicine_delivered">ส่งยาไปที่บ้านแล้ว</StatusTag>
            default:
                return <StatusTag>{status}</StatusTag>
        }
    }

    const handleUpdateStatus = (record) => {
        setSelectedRecord(record)
        setViewModalVisible(true)
    }

    const handleRevertStatus = async (record) => {
        try {
            await http.patch(`/PatientRegistrations/${record.id}`, {
                status: 'sent_to_pharmacy'
            })
            fetchPharmacyQueue()
        } catch (error) {
            console.error('Error reverting status:', error)
        }
    }

    // เพิ่มฟังก์ชันใหม่สำหรับส่งยา
    const handleDeliverMedicine = async (record) => {
        try {
            await http.patch(`/PatientRegistrations/${record.id}`, {
                status: 'medicine_delivered',
                deliveredAt: new Date().toISOString()
            })
            fetchPharmacyQueue()
        } catch (error) {
            console.error('Error delivering medicine:', error)
        }
    }

    const handleView = async (record) => {
        setSelectedRecord(record)
        setViewModalVisible(true)

        // เรียก API เพื่อดึงข้อมูลการเยี่ยมผู้ป่วย
        if (record.additionalData?.cId) {
            try {
                setLoadingVisitData(true)

                // เรียก API โดยตรงแทนการใช้ UserInfo model
                const response = await http.get('/UserInfos/checkUserVisitedByVerifiedFromHIEByCid', {
                    params: {
                        cId: record.additionalData.cId
                    }
                })

                setPatientVisitData(response.data)
            } catch (error) {
                console.error('Error fetching visit data:', error)
                setPatientVisitData(null)
            } finally {
                setLoadingVisitData(false)
            }
        } else {
            console.log('No cId found in record:', record)
        }
    }

    const handleLogout = () => {

        history.push('/logout')
    }

    const filteredData = data.filter(item => {
        const matchesSearch = !searchText ||
            (item.additionalData?.firstname && item.additionalData.firstname.toLowerCase().includes(searchText.toLowerCase())) ||
            (item.additionalData?.lastname && item.additionalData.lastname.toLowerCase().includes(searchText.toLowerCase())) ||
            (item.additionalData?.hnId && item.additionalData.hnId.toString().includes(searchText)) ||
            (item.additionalData?.cId && item.additionalData.cId.toString().includes(searchText))

        const matchesStatus = statusFilter === 'all' || item.status === statusFilter

        return matchesSearch && matchesStatus
    })

    const columns = [
        {
            title: 'ชื่อ-นามสกุล',
            key: 'name',
            width: 200,
            render: (_, record) => {
                const firstname = record.additionalData?.firstname || record.firstname || '-'
                const lastname = record.additionalData?.lastname || record.lastname || '-'
                return (
                    <div>
                        <div style={{ fontWeight: '600', color: '#1890ff' }}>{firstname} {lastname}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            HN: {record.additionalData?.hnId || record.hnId || '-'}
                        </div>
                    </div>
                )
            }
        },
        {
            title: 'เลขบัตรประชาชน',
            dataIndex: ['additionalData', 'cId'],
            key: 'cId',
            width: 150,
            render: (cId) => (
                <Text code style={{ background: '#f0f0f0', padding: '2px 6px', borderRadius: '4px' }}>
                    {cId || '-'}
                </Text>
            )
        },
        {
            title: 'เบอร์โทรศัพท์',
            dataIndex: ['additionalData', 'mobileNumber'],
            key: 'mobileNumber',
            width: 140,
            render: (mobile) => mobile || '-'
        },
        {
            title: 'สถานะ',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status) => getStatusTag(status)
        },
        {
            title: 'วันที่ส่งมา',
            dataIndex: 'sentToPharmacyAt',
            key: 'sentToPharmacyAt',
            width: 150,
            render: (date) => date ? new Date(date).toLocaleDateString('th-TH') : '-'
        },
        {
            title: 'วันที่ส่งยา',
            dataIndex: 'deliveredAt',
            key: 'deliveredAt',
            width: 150,
            render: (date) => date ? new Date(date).toLocaleDateString('th-TH') : '-'
        },
        {
            title: 'ความเร่งด่วน',
            dataIndex: 'priority',
            key: 'priority',
            width: 120,
            render: (priority) => (
                <Tag color={priority === 'high' ? 'red' : priority === 'medium' ? 'orange' : 'default'}>
                    {priority === 'high' ? 'สูง' : priority === 'medium' ? 'ปานกลาง' : 'ปกติ'}
                </Tag>
            )
        },
        {
            title: 'การดำเนินการ',
            key: 'action',
            width: 200,
            render: (_, record) => (
                <Space>
                    <ActionButton
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record)}
                        size="small"
                    >
                        ดูรายละเอียด
                    </ActionButton>
                    {record.status === 'sent_to_pharmacy' && (
                        <Popconfirm
                            title="ยืนยันการส่งยา"
                            description="ต้องการเปลี่ยนสถานะเป็น 'ส่งยาไปที่บ้านแล้ว' หรือไม่?"
                            onConfirm={() => handleDeliverMedicine(record)}
                            okText="ยืนยัน"
                            cancelText="ยกเลิก"
                        >
                            <ActionButton
                                type="primary"
                                icon={<HomeOutlined />}
                                size="small"
                                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                            >
                                ส่งยา
                            </ActionButton>
                        </Popconfirm>
                    )}
                    {record.status === 'medicine_delivered' && (
                        <Popconfirm
                            title="ยืนยันการเปลี่ยนสถานะ"
                            description="ต้องการเปลี่ยนสถานะกลับเป็น 'รอส่งยา' หรือไม่?"
                            onConfirm={() => handleRevertStatus(record)}
                            okText="ยืนยัน"
                            cancelText="ยกเลิก"
                        >
                            <ActionButton
                                type="default"
                                icon={<ReloadOutlined />}
                                size="small"
                            >
                                เปลี่ยนสถานะ
                            </ActionButton>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ]

    // คำนวณสถิติ
    const totalPatients = filteredData.length
    const pendingDelivery = filteredData.filter(item => item.status === 'sent_to_pharmacy').length
    const delivered = filteredData.filter(item => item.status === 'medicine_delivered').length
    const highPriority = filteredData.filter(item => item.priority === 'high').length

    return (
        <Container>
            {/* Logout Button */}
            <LogoutButton
                type="primary"
                danger
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                size="large"
            >
                ออกจากระบบ
            </LogoutButton>

            {/* Header with Statistics */}
            <HeaderCard>
                <Row gutter={24}>
                    <Col span={6}>
                        <Statistic
                            title="ผู้ป่วยทั้งหมด"
                            value={totalPatients}
                            valueStyle={{ color: 'white', fontSize: '28px' }}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="รอส่งยา"
                            value={pendingDelivery}
                            valueStyle={{ color: '#ffd591', fontSize: '28px' }}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="ส่งยาแล้ว"
                            value={delivered}
                            valueStyle={{ color: '#b7eb8f', fontSize: '28px' }}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="ความเร่งด่วนสูง"
                            value={highPriority}
                            valueStyle={{ color: '#ff7875', fontSize: '28px' }}
                        />
                    </Col>
                </Row>
            </HeaderCard>

            {/* Filters */}
            <FilterCard>
                <Row gutter={16} align="middle">
                    <Col span={8}>
                        <Search
                            placeholder="ค้นหาชื่อ, HN, หรือเลขบัตรประชาชน"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            prefix={<SearchOutlined />}
                            size="large"
                            style={{ borderRadius: '8px' }}
                        />
                    </Col>
                    <Col span={4}>
                        <Select
                            value={statusFilter}
                            onChange={setStatusFilter}
                            size="large"
                            style={{ width: '100%', borderRadius: '8px' }}
                            suffixIcon={<FilterOutlined />}
                        >
                            <Option value="all">ทั้งหมด</Option>
                            <Option value="sent_to_pharmacy">รอส่งยา</Option>
                            <Option value="medicine_delivered">ส่งยาแล้ว</Option>
                        </Select>
                    </Col>
                    <Col span={4}>
                        <ActionButton
                            icon={<ReloadOutlined />}
                            onClick={fetchPharmacyQueue}
                            loading={loading}
                            size="large"
                            style={{ width: '100%' }}
                        >
                            รีเฟรช
                        </ActionButton>
                    </Col>
                </Row>
            </FilterCard>

            {/* Table */}
            <TableCard>
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        total: filteredData.length,
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} จาก ${total} รายการ`,
                        pageSizeOptions: ['10', '20', '50', '100']
                    }}
                    scroll={{ x: 1200 }}
                    size="middle"
                />
            </TableCard>

            {/* View Modal - ใช้โค้ดเดิมที่ปรับปรุงแล้ว */}
            <Modal
                title="รายละเอียดผู้ป่วย"
                open={viewModalVisible}
                onCancel={() => {
                    setViewModalVisible(false)
                    setPatientVisitData(null)
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setViewModalVisible(false)
                        setPatientVisitData(null)
                    }}>
                        ปิด
                    </Button>
                ]}
                width={1400}
                style={{ top: 20 }}
            >
                {selectedRecord && (
                    <div style={{ display: 'flex', gap: '24px', height: '70vh' }}>
                        {/* Panel ซ้าย - ข้อมูลผู้ป่วย */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            paddingRight: '16px',
                            background: '#fafafa',
                            borderRadius: '8px',
                            padding: '20px'
                        }}>
                            <div style={{ marginBottom: '24px' }}>
                                <h3 style={{
                                    color: '#1890ff',
                                    marginBottom: '16px',
                                    borderBottom: '2px solid #1890ff',
                                    paddingBottom: '8px'
                                }}>
                                    ข้อมูลส่วนตัว
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div style={{ background: 'white', padding: '12px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <strong style={{ color: '#666' }}>ชื่อ:</strong> {selectedRecord.additionalData?.firstname || selectedRecord.firstname}
                                    </div>
                                    <div style={{ background: 'white', padding: '12px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <strong style={{ color: '#666' }}>นามสกุล:</strong> {selectedRecord.additionalData?.lastname || selectedRecord.lastname}
                                    </div>
                                    <div style={{ background: 'white', padding: '12px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <strong style={{ color: '#666' }}>HN ID:</strong> {selectedRecord.additionalData?.hnId || selectedRecord.hnId}
                                    </div>
                                    <div style={{ background: 'white', padding: '12px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <strong style={{ color: '#666' }}>เลขบัตรประชาชน:</strong> {selectedRecord.additionalData?.cId || '-'}
                                    </div>
                                    <div style={{ background: 'white', padding: '12px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <strong style={{ color: '#666' }}>เบอร์โทรศัพท์:</strong> {selectedRecord.additionalData?.mobileNumber || selectedRecord.mobileNumber || '-'}
                                    </div>
                                    <div style={{ background: 'white', padding: '12px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <strong style={{ color: '#666' }}>เพศ:</strong> {selectedRecord.additionalData?.gender === 'male' ? 'ชาย' : 'หญิง'}
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <h3 style={{
                                    color: '#52c41a',
                                    marginBottom: '16px',
                                    borderBottom: '2px solid #52c41a',
                                    paddingBottom: '8px'
                                }}>
                                    สถานะการส่งยา
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <div style={{ background: 'white', padding: '12px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <strong style={{ color: '#666' }}>สถานะ:</strong> {getStatusTag(selectedRecord.status)}
                                    </div>
                                    <div style={{ background: 'white', padding: '12px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <strong style={{ color: '#666' }}>ความเร่งด่วน:</strong> {selectedRecord.priority || 'ปกติ'}
                                    </div>
                                    <div style={{ background: 'white', padding: '12px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <strong style={{ color: '#666' }}>วันที่ส่งมา:</strong> {selectedRecord.sentToPharmacyAt ? new Date(selectedRecord.sentToPharmacyAt).toLocaleString('th-TH') : '-'}
                                    </div>
                                    <div style={{ background: 'white', padding: '12px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <strong style={{ color: '#666' }}>วันที่ส่งยา:</strong> {selectedRecord.deliveredAt ? new Date(selectedRecord.deliveredAt).toLocaleString('th-TH') : '-'}
                                    </div>
                                </div>
                                {selectedRecord.pharmacyNotes && (
                                    <div style={{
                                        background: 'white',
                                        padding: '12px',
                                        borderRadius: '6px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        marginTop: '12px'
                                    }}>
                                        <strong style={{ color: '#666' }}>หมายเหตุการส่งยา:</strong> {selectedRecord.pharmacyNotes}
                                    </div>
                                )}
                            </div>

                            {selectedRecord.additionalData?.address && (
                                <div style={{ marginBottom: '24px' }}>
                                    <h3 style={{
                                        color: '#722ed1',
                                        marginBottom: '16px',
                                        borderBottom: '2px solid #722ed1',
                                        paddingBottom: '8px'
                                    }}>
                                        ที่อยู่
                                    </h3>
                                    <div style={{ background: 'white', padding: '16px', borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <p><strong style={{ color: '#666' }}>ที่อยู่:</strong> {selectedRecord.additionalData.address.address}</p>
                                        <p><strong style={{ color: '#666' }}>แขวง/ตำบล:</strong> {selectedRecord.additionalData.address.address2}</p>
                                        <p><strong style={{ color: '#666' }}>เขต/อำเภอ:</strong> {selectedRecord.additionalData.address.area}</p>
                                        <p><strong style={{ color: '#666' }}>จังหวัด:</strong> {selectedRecord.additionalData.address.province}</p>
                                        <p><strong style={{ color: '#666' }}>รหัสไปรษณีย์:</strong> {selectedRecord.additionalData.address.postalCode}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Panel ขวา - ข้อมูลการเยี่ยมผู้ป่วย */}
                        <div style={{
                            flex: 1,
                            borderLeft: '2px solid #e8e8e8',
                            paddingLeft: '24px',
                            overflowY: 'auto',
                            background: '#fafafa',
                            borderRadius: '8px',
                            padding: '20px'
                        }}>
                            <h3 style={{
                                color: '#fa8c16',
                                marginBottom: '20px',
                                borderBottom: '2px solid #fa8c16',
                                paddingBottom: '8px'
                            }}>
                                ข้อมูลการเยี่ยมผู้ป่วย
                            </h3>
                            {loadingVisitData ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <Spin size="large" />
                                    <p style={{ marginTop: '16px', color: '#666' }}>กำลังโหลดข้อมูลการเยี่ยมผู้ป่วย...</p>
                                </div>
                            ) : patientVisitData && patientVisitData.docs ? (
                                <div>
                                    <div style={{
                                        background: 'white',
                                        padding: '16px',
                                        borderRadius: '8px',
                                        marginBottom: '16px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}>
                                        <h4 style={{ margin: 0, color: '#1890ff' }}>
                                            รายการการเยี่ยม ({patientVisitData.totalDocs} รายการ)
                                        </h4>
                                    </div>
                                    <Table
                                        dataSource={patientVisitData.docs}
                                        rowKey="_id"
                                        pagination={false}
                                        scroll={{ x: 600, y: 400 }}
                                        size="small"
                                        style={{
                                            background: 'white',
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}
                                        columns={[
                                            {
                                                title: 'VN',
                                                dataIndex: 'vn',
                                                key: 'vn',
                                                width: 150,
                                                render: (vn) => (
                                                    <span style={{
                                                        background: '#1890ff',
                                                        color: 'white',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {vn || '-'}
                                                    </span>
                                                )
                                            },
                                            {
                                                title: 'เวลาเยี่ยม',
                                                dataIndex: 'visitDateTime',
                                                key: 'visitDateTime',
                                                width: 140,
                                                render: (dateTime) => dateTime ? new Date(dateTime).toLocaleString('th-TH') : '-'
                                            },
                                            {
                                                title: 'แผนก',
                                                dataIndex: 'spcltyName',
                                                key: 'spcltyName',
                                                width: 120,
                                                render: (spclty) => (
                                                    <span style={{
                                                        background: '#f0f0f0',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px'
                                                    }}>
                                                        {spclty || '-'}
                                                    </span>
                                                )
                                            },
                                            {
                                                title: 'แพทย์',
                                                dataIndex: 'doctorName',
                                                key: 'doctorName',
                                                width: 180,
                                                render: (doctor) => doctor || '-'
                                            },
                                            {
                                                title: 'รายการยา',
                                                key: 'action',
                                                width: 100,
                                                render: (_, record) => {
                                                    const hasDrugs = record.drugs && record.drugs.length > 0

                                                    return hasDrugs ? (
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            icon={<MedicineBoxOutlined />}
                                                            onClick={() => {
                                                                setSelectedVisitRecord(record)
                                                                setDrugModalVisible(true)
                                                            }}
                                                            style={{
                                                                borderRadius: '4px',
                                                                fontSize: '11px'
                                                            }}
                                                        >
                                                            ดูยา
                                                        </Button>
                                                    ) : (
                                                        <span style={{
                                                            color: '#999',
                                                            fontSize: '12px',
                                                            fontStyle: 'italic'
                                                        }}>
                                                            ไม่มียา
                                                        </span>
                                                    )
                                                }
                                            }
                                        ]}
                                    />
                                </div>
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '40px',
                                    background: 'white',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}>
                                    <p style={{ color: '#999', fontSize: '16px' }}>ไม่พบข้อมูลการเยี่ยมผู้ป่วย</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            {/* Drug Details Modal - ใช้โค้ดเดิมที่ปรับปรุงแล้ว */}
            <Modal
                title="รายการยา"
                open={drugModalVisible}
                onCancel={() => {
                    setDrugModalVisible(false)
                    setSelectedVisitRecord(null)
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setDrugModalVisible(false)
                        setSelectedVisitRecord(null)
                    }}>
                        ปิด
                    </Button>
                ]}
                width={1000}
                style={{ top: 20 }}
            >
                {selectedVisitRecord && (
                    <div>
                        <div style={{
                            background: '#f0f8ff',
                            padding: '16px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid #1890ff'
                        }}>
                            <h4 style={{ margin: 0, color: '#1890ff' }}>
                                รายการยา - VN: {selectedVisitRecord.vn}
                            </h4>
                            <p style={{ margin: '8px 0 0 0', color: '#666' }}>
                                <strong>วันที่เยี่ยม:</strong> {selectedVisitRecord.vstdate ? new Date(selectedVisitRecord.vstdate).toLocaleDateString('th-TH') : '-'} |
                                <strong> แพทย์:</strong> {selectedVisitRecord.doctorName || '-'}
                            </p>
                        </div>

                        <Table
                            dataSource={selectedVisitRecord.drugs || []}
                            rowKey="_id"
                            pagination={false}
                            scroll={{ x: 800, y: 400 }}
                            size="small"
                            style={{
                                background: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                            columns={[
                                {
                                    title: 'ชื่อยา',
                                    dataIndex: 'drugNondugName',
                                    key: 'drugNondugName',
                                    width: 200,
                                    render: (name) => (
                                        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                                            {name || '-'}
                                        </span>
                                    )
                                },
                                {
                                    title: 'ความแรง',
                                    dataIndex: 'strength',
                                    key: 'strength',
                                    width: 100,
                                    render: (strength) => strength || '-'
                                },
                                {
                                    title: 'รูปแบบ',
                                    dataIndex: 'medform_name',
                                    key: 'medform_name',
                                    width: 100,
                                    render: (form) => form || '-'
                                },
                                {
                                    title: 'จำนวน',
                                    dataIndex: 'qty',
                                    key: 'qty',
                                    width: 80,
                                    render: (qty) => qty || '-'
                                },
                                {
                                    title: 'หน่วย',
                                    dataIndex: 'meduseunit_name',
                                    key: 'meduseunit_name',
                                    width: 100,
                                    render: (unit) => unit || '-'
                                },
                                {
                                    title: 'วิธีใช้',
                                    dataIndex: 'drugUsage',
                                    key: 'drugUsage',
                                    width: 200,
                                    render: (usage) => usage || '-'
                                },
                                {
                                    title: 'ประเภท',
                                    dataIndex: 'medtype_name',
                                    key: 'medtype_name',
                                    width: 120,
                                    render: (type) => type || '-'
                                },
                                {
                                    title: 'สถานะ',
                                    dataIndex: 'prscst_name',
                                    key: 'prscst_name',
                                    width: 100,
                                    render: (status) => (
                                        <Tag color={status === 'จ่ายยาแล้ว' ? 'green' : 'orange'}>
                                            {status || '-'}
                                        </Tag>
                                    )
                                },
                                {
                                    title: 'ราคา',
                                    dataIndex: 'sumPrice',
                                    key: 'sumPrice',
                                    width: 80,
                                    render: (price) => price ? `${price} บาท` : '-'
                                }
                            ]}
                        />
                    </div>
                )}
            </Modal>
        </Container>
    )
}

export default PharmacyQueue
